import React, { useEffect, useMemo, useState } from 'react';
import Type from 'prop-types';
import InputMask from 'react-input-mask';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, TextField, Typography, } from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DayPicker } from 'react-day-picker';
import { format, isValid, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import { DialogStyles, RangeCalendarStyles as sx } from './styled';

const defaultLocale = ru;
const INPUT_DATE_FORMAT = 'dd.MM.yyyy';
const HEADER_DATE_FORMAT = 'd MMM';
const EMPTY_RANGE = { from: undefined, to: undefined };

const toRange = (value) => {
  const [from, to] = Array.isArray(value) ? value : [null, null];

  return { from: from || undefined, to: to || undefined };
};

const formatInputDate = (date) => {
  return date ? format(date, INPUT_DATE_FORMAT) : '';
};

const formatHeaderDate = (date, locale) => {
  return date ? format(date, HEADER_DATE_FORMAT, { locale }) : '—';
};

const parseInputDate = (value) => {
  if (!value || value.includes('_')) {
    return undefined;
  }

  const parsedDate = parse(value, INPUT_DATE_FORMAT, new Date());

  if (!isValid(parsedDate)) {
    return undefined;
  }

  return parsedDate;
};

const EditModeInput = ({ value, onChange, label }) => {
  return (
    <InputMask mask="99.99.9999" value={value} onChange={onChange}>
      <TextField
        variant="standard"
        label={label}
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={sx.input}
      />
    </InputMask>
  );
};

EditModeInput.propTypes = {
  value: Type.string.isRequired,
  onChange: Type.func.isRequired,
  label: Type.string.isRequired,
};

const RangeCalendar = ({
  open,
  value,
  onClose,
  onApply,
  onClear,
  startLabel,
  endLabel,
  applyLabel,
  cancelLabel,
  clearLabel,
  title,
  locale,
}) => {
  const [draftRange, setDraftRange] = useState(EMPTY_RANGE);
  const [isEditMode, setIsEditMode] = useState(false);
  const [inputValues, setInputValues] = useState({ from: '', to: '' });

  useEffect(() => {
    const nextRange = toRange(value);

    setDraftRange(nextRange);
    setInputValues({
      from: formatInputDate(nextRange.from),
      to: formatInputDate(nextRange.to),
    });
    setIsEditMode(false);
  }, [value, open]);

  const activeLocale = locale || defaultLocale;

  const headerValue = useMemo(() => {
    return `${formatHeaderDate(
      draftRange.from,
      activeLocale,
    )} — ${formatHeaderDate(draftRange.to, activeLocale)}`;
  }, [activeLocale, draftRange.from, draftRange.to]);

  const hasFullRange = Boolean(draftRange.from && draftRange.to);

  const handleApply = () => {
    onApply([draftRange.from || null, draftRange.to || null]);
  };

  const handleClear = () => {
    setDraftRange(EMPTY_RANGE);
    setInputValues({ from: '', to: '' });
    onClear();
  };

  const handleInputChange = (key) => (event) => {
    const nextValue = event.target.value;
    const nextInputs = { ...inputValues, [key]: nextValue };
    const from = parseInputDate(nextInputs.from);
    const to = parseInputDate(nextInputs.to);

    setInputValues(nextInputs);
    setDraftRange({ from, to });
  };

  const renderHeader = () => {
    return (
      <Box sx={sx.header}>
        <Box>
          <Typography sx={sx.title}>{title}</Typography>
          <Box sx={sx.headerSummaryRow}>
            <Typography sx={sx.headerValue}>{headerValue}</Typography>
            <IconButton
              sx={sx.headerIconButton}
              onClick={() => setIsEditMode((prev) => !prev)}
              size="small"
            >
              <EditIcon sx={sx.headerIcon} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderEditMode = () => {
    return (
      <Box sx={sx.editMode}>
        <EditModeInput
          value={inputValues.from}
          onChange={handleInputChange('from')}
          label={startLabel}
        />
        <EditModeInput
          value={inputValues.to}
          onChange={handleInputChange('to')}
          label={endLabel}
        />
        <IconButton
          sx={sx.modeSwitchButton}
          onClick={() => setIsEditMode(false)}
        >
          <CalendarTodayIcon sx={sx.modeSwitchIcon} />
        </IconButton>
      </Box>
    );
  };

  const renderCalendar = () => {
    return (
      <Box sx={sx.calendarWrapper}>
        <DayPicker
          mode="range"
          numberOfMonths={1}
          locale={activeLocale}
          selected={draftRange}
          onSelect={setDraftRange}
          showOutsideDays
          fixedWeeks
          classNames={{
            root: 'RangeCalendar-root',
            months: 'RangeCalendar-months',
            month: 'RangeCalendar-month',
            month_caption: 'RangeCalendar-monthCaption',
            caption_label: 'RangeCalendar-monthCaptionLabel',
            nav: 'RangeCalendar-nav',
            button_previous: 'RangeCalendar-navButton',
            button_next: 'RangeCalendar-navButton',
            chevron: 'RangeCalendar-chevron',
            month_grid: 'RangeCalendar-table',
            weekdays: 'RangeCalendar-weekdays',
            weekday: 'RangeCalendar-weekday',
            weeks: 'RangeCalendar-weeks',
            week: 'RangeCalendar-week',
            day: 'RangeCalendar-day',
            day_button: 'RangeCalendar-dayButton',
            outside: 'RangeCalendar-outside',
            selected: 'RangeCalendar-selected',
            range_start: 'RangeCalendar-rangeStart',
            range_middle: 'RangeCalendar-rangeMiddle',
            range_end: 'RangeCalendar-rangeEnd',
            today: 'RangeCalendar-today',
          }}
          components={{
            Chevron: ({ orientation, className, disabled, ...other }) => {
              const Icon =
                orientation === 'left' ? ChevronLeftIcon : ChevronRightIcon;

              return (
                <Icon
                  className={className}
                  {...other}
                  color={disabled ? 'disabled' : 'inherit'}
                />
              );
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: sx.dialogPaper }}
      sx={DialogStyles}
    >
      {renderHeader()}
      <DialogContent sx={sx.content}>
        {isEditMode ? renderEditMode() : renderCalendar()}
      </DialogContent>
      <DialogActions sx={sx.actions}>
        <Button onClick={handleClear}>{clearLabel}</Button>
        <Button onClick={onClose}>{cancelLabel}</Button>
        <Button onClick={handleApply} color="primary" disabled={!hasFullRange}>
          {applyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
RangeCalendar.propTypes = {
  open: Type.bool.isRequired,
  value: Type.arrayOf(
    Type.oneOfType([Type.instanceOf(Date), Type.oneOf([null])]),
  ),
  onClose: Type.func.isRequired,
  onApply: Type.func.isRequired,
  onClear: Type.func.isRequired,
  startLabel: Type.string.isRequired,
  endLabel: Type.string.isRequired,
  applyLabel: Type.string.isRequired,
  cancelLabel: Type.string.isRequired,
  clearLabel: Type.string.isRequired,
  title: Type.string,
  locale: Type.object,
};

RangeCalendar.defaultProps = {
  value: [null, null],
  title: '',
  locale: defaultLocale,
};

export default RangeCalendar;
